import { useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import {
  useQuery as useGqtyQuery,
  query as gqtyQuery,
  resolved,
} from '@hapana/shared/web/services/gqty'

import { GENERIC_ERROR_KEY } from '@hapana/shared/constants/errors.constants'

import { useErrorHandler } from './errors.hooks'
import { useSiteId, useSiteIdResolved } from './site.hooks'

const fetchClientNotes = (siteId, clientId) =>
  resolved(
    () => {
      const result = gqtyQuery.clientNotes({ clientId, siteId })
      const notes = result?.notes ?? []
      notes.forEach((note) => {
        void note?.noteId
        void note?.content
        void note?.createdAt
        void note?.createdByName
      })
      return notes
    },
    { refetch: true }
  )

export const useClientNotesAndProperties = (clientId, siteIdOverride) => {
  // OC-173: prefer an explicitly-passed siteId. useSiteIdResolved() falls back to a non-reactive
  // localStorage read (populated async, without a re-render) and to a `/s/<id>` URL match that the
  // barcode check-in route (`/p/barcode/<id>`) doesn't hit — so on first open siteId was '' and the
  // notes never fetched until a page reload. Callers that know the site (the check-in modal) pass it.
  const resolvedSiteId = useSiteIdResolved()
  const siteId = siteIdOverride || resolvedSiteId
  const [data, setData] = useState({ clientSiteNotes: [], clientSiteAlertedProperties: [] })
  const [isLoading, setIsLoading] = useState(() => Boolean(clientId && siteId))

  useEffect(() => {
    if (!clientId || !siteId) {
      return
    }

    let cancelled = false
    setIsLoading(true)

    fetchClientNotes(siteId, clientId)
      .then((notes) => {
        if (cancelled) {
          return
        }
        setData({
          clientSiteNotes: (notes || []).map((note) => ({
            id: note?.noteId,
            text: note?.content,
            date: note?.createdAt,
            name: note?.createdByName,
          })),
          clientSiteAlertedProperties: [],
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading, error: null, refetch: () => Promise.resolve(null) }
}

export const useClientPendingAgreements = (clientId) => {
  const { data: siteId } = useSiteId()
  const { onError } = useErrorHandler(GENERIC_ERROR_KEY)
  const query = useGqtyQuery({
    suspense: false,
    cachePolicy: 'no-cache',
  })
  const shouldFetch = Boolean(clientId && siteId)

  useEffect(() => {
    if (query.$state.error && shouldFetch) {
      onError(query.$state.error)
    }
  }, [query.$state.error, onError, shouldFetch])

  const pendingAgreements = shouldFetch
    ? query.client({ id: clientId, siteId })?.client?.pendingAgreements
    : []

  const data = useMemo(() => {
    if (!shouldFetch) {
      return []
    }

    return (pendingAgreements || []).map((agreement, idx) => ({
      // packageID is aliased from agreementName in the DB query
      id: agreement?.packageSubscriptionID || `agreement-${idx}`,
      name: agreement?.packageID || 'Pending Agreement',
    }))
  }, [pendingAgreements, shouldFetch])

  return {
    data,
    isLoading: shouldFetch ? query.$state.isLoading : false,
  }
}

const ACTIVE_PACKAGE_STATUSES = ['Active', 'PendingStartDate']
const ACTIVE_PACKAGE_TYPES = ['PACKAGE', 'MEMBERSHIP', 'GUEST_PASS']
const HAS_ACTIVE_PACKAGE_STALE_TIME_MS = 30_000
const HAS_ACTIVE_PACKAGE_BATCH_WINDOW_MS = 10

const fetchActivePackages = (siteId, clientId) =>
  resolved(
    () => {
      const result = gqtyQuery.clientServices({
        clientId,
        siteId,
        // OC-119: include claimed guest-pass services so this widget agrees with
        // the client profile Packages tab, which also lists GUEST_PASS.
        status: ACTIVE_PACKAGE_STATUSES,
        type: ACTIVE_PACKAGE_TYPES,
        pageSize: 20,
      })
      void result?.pageInfo?.totalRecords
      const rows = result?.results ?? []
      rows.forEach((r) => {
        void r.cursor
        void r.row?.serviceId
        void r.row?.serviceTemplateName
        void r.row?.serviceTemplateType
        void r.row?.siteId
        void r.row?.clientId
        void r.row?.status
        void r.row?.creditBalanceSnapshot?.totalBalance
        void r.row?.creditBalanceSnapshot?.hasUnlimited
        void r.row?.lastUsedOn
        void r.row?.creditBalanceSnapshot?.allocations
        r.row?.creditBalanceSnapshot?.allocations?.forEach((a) => {
          void a?.allocated
          void a?.consumed
          void a?.balance
        })
      })
      return rows
    },
    { refetch: true }
  )

const pendingActivePackageLookups = new Map()

const flushActivePackageLookups = async (siteId) => {
  const lookups = pendingActivePackageLookups.get(siteId)
  pendingActivePackageLookups.delete(siteId)
  const clientIds = [...lookups.keys()]

  try {
    const results = await resolved(
      () =>
        clientIds.map((clientId) => {
          const rows =
            gqtyQuery.clientServices({
              clientId,
              siteId,
              status: ACTIVE_PACKAGE_STATUSES,
              type: ACTIVE_PACKAGE_TYPES,
              pageSize: 1,
            })?.results ?? []
          rows.forEach((r) => {
            void r.row?.serviceId
          })
          return rows.some((r) => r.row?.serviceId)
        }),
      { refetch: true }
    )
    clientIds.forEach((clientId, index) => lookups.get(clientId).resolve(results[index]))
  } catch (error) {
    lookups.forEach(({ reject }) => reject(error))
  }
}

const fetchHasActivePackage = (siteId, clientId) => {
  let lookups = pendingActivePackageLookups.get(siteId)
  if (!lookups) {
    lookups = new Map()
    pendingActivePackageLookups.set(siteId, lookups)
    setTimeout(() => flushActivePackageLookups(siteId), HAS_ACTIVE_PACKAGE_BATCH_WINDOW_MS)
  }
  return new Promise((resolve, reject) => {
    lookups.set(clientId, { resolve, reject })
  })
}

const mapPackageRow = (r, siteName = '', v1ClientId = null) => {
  const snapshot = r.row?.creditBalanceSnapshot
  const firstAlloc = snapshot?.allocations?.[0]
  const creditsTotal = snapshot?.hasUnlimited ? 1000 : (firstAlloc?.allocated ?? 0)
  const creditsConsumed = firstAlloc?.consumed ?? 0

  return {
    id: r.row?.serviceId,
    siteId: r.row?.siteId ?? null,
    clientId: r.row?.clientId ?? null,
    creditsConsumed,
    creditsTotal,
    lastUsedOn: r.row?.lastUsedOn ?? null,
    packageDefinition: {
      name: r.row?.serviceTemplateName || '',
      type: r.row?.serviceTemplateType || '',
    },
    site: siteName ? { name: siteName } : null,
    client: v1ClientId != null ? { v1Id: v1ClientId } : null,
  }
}

export const useClientHasActivePackage = (clientId, siteIdOverride) => {
  const resolvedSiteId = useSiteIdResolved()
  const siteId = siteIdOverride || resolvedSiteId

  const { data } = useQuery(
    ['clientHasActivePackage', siteId, clientId],
    () => fetchHasActivePackage(siteId, clientId),
    {
      enabled: Boolean(clientId && siteId),
      staleTime: HAS_ACTIVE_PACKAGE_STALE_TIME_MS,
      refetchOnWindowFocus: false,
    }
  )

  return { data: data ?? false }
}

export const useClientActivePackages = (clientId, siteIdOverride) => {
  // OC-173: same fix as useClientNotesAndProperties — prefer an explicitly-passed siteId over the
  // non-reactive useSiteIdResolved() fallback, which is '' on first open of the barcode check-in route.
  const resolvedSiteId = useSiteIdResolved()
  const siteId = siteIdOverride || resolvedSiteId
  const [data, setData] = useState({ clientSiteActivePackages: [] })
  const [isLoading, setIsLoading] = useState(() => Boolean(clientId && siteId))

  useEffect(() => {
    if (!clientId || !siteId) {
      return
    }

    let cancelled = false
    setIsLoading(true)

    Promise.all([fetchActivePackages(siteId, clientId), fetchSiteName(siteId)])
      .then(([rows, siteName]) => {
        if (cancelled) {
          return
        }
        setData({
          clientSiteActivePackages: (rows || [])
            .filter((r) => r.row?.serviceId)
            .map((r) => mapPackageRow(r, siteName)),
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading, error: null, refetch: () => Promise.resolve(null) }
}

const FAILED_STATUSES = ['Failed', 'FailedRetry']
const OUTSTANDING_STATUSES = ['Staged', 'Scheduled', 'Processing', 'RequiresAction']

const fetchInvoicesByStatuses = (siteId, clientId, statuses) =>
  resolved(
    () => {
      const result = gqtyQuery.invoices({
        siteId,
        pageSize: 20,
        filter: { clientId, statuses },
      })
      void result?.pageInfo?.totalRecords
      const rows = result?.results ?? []
      rows.forEach((r) => {
        void r.cursor
        void r.row?.invoiceId
        void r.row?.description
        void r.row?.membershipName
        void r.row?.productName
        void r.row?.totalAmount
        void r.row?.currency
        void r.row?.createdAt
        void r.row?.saleDate
        void r.row?.siteId
        void r.row?.isRecurring
      })
      return rows
    },
    { refetch: true }
  )

const fetchSiteName = (siteId) =>
  resolved(
    () => {
      return gqtyQuery.site({ siteId })?.name ?? ''
    },
    { refetch: true }
  )

const toISODatetime = (v) => {
  if (!v) {
    return null
  }
  const d = v instanceof Date ? v : new Date(String(v))
  if (isNaN(d.getTime())) {
    return null
  }
  return d.toISOString()
}

const mapInvoiceRow = (r, siteName = '') => ({
  id: r.row?.invoiceId,
  invoiceId: r.row?.invoiceId,
  label: r.row?.membershipName || r.row?.description || r.row?.productName || '',
  currency: r.row?.currency || 'AUD',
  price: r.row?.totalAmount,
  siteName,
  datetime: toISODatetime(r.row?.createdAt) || toISODatetime(r.row?.saleDate) || null,
  v1PaymentType: r.row?.isRecurring ? 'RECURRING' : 'ONE_TIME',
})

export const useClientFailedPayments = (clientId) => {
  const siteId = useSiteIdResolved()
  const [data, setData] = useState({ failedPayments: [], outstandingPayments: [] })
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    if (!clientId || !siteId) {
      return
    }

    let cancelled = false
    setIsLoading(true)

    Promise.all([
      fetchInvoicesByStatuses(siteId, clientId, FAILED_STATUSES),
      fetchInvoicesByStatuses(siteId, clientId, OUTSTANDING_STATUSES),
      fetchSiteName(siteId),
    ])
      .then(([failedRows, outstandingRows, siteName]) => {
        if (cancelled) {
          return
        }
        setData({
          failedPayments: (failedRows || [])
            .filter((r) => r.row?.invoiceId)
            .map((r) => mapInvoiceRow(r, siteName)),
          outstandingPayments: (outstandingRows || [])
            .filter((r) => r.row?.invoiceId)
            .map((r) => mapInvoiceRow(r, siteName)),
        })
        setIsLoading(false)
      })
      .catch(() => {
        if (!cancelled) {
          setIsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [clientId, siteId])

  return { data, isLoading }
}
